import { Search, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface StaffMember {
	id: number;
	email: string;
	name: string;
	department: string;
	role: string;
}

interface Props {
	staff: StaffMember[];
	assigned: StaffMember[];
	search: string;
	onSearchChange: (value: string) => void;
	onAdd: (member: StaffMember) => void;
	onClose: () => void;
}

export function StaffSearchModal({
	staff,
	assigned,
	search,
	onSearchChange,
	onAdd,
	onClose,
}: Props) {
	const { t } = useTranslation(['medical', 'common']);

	const filtered = useMemo(
		() =>
			search
				? staff.filter(
						(s) => s.name.includes(search) || s.department.includes(search),
					)
				: staff,
		[staff, search],
	);

	return (
		<div
			className='modal-overlay modal-overlay--visible z-[310]'
			role='dialog'
			aria-modal='true'
		>
			<div className='modal !min-w-[480px] !max-w-[560px]'>
				<div className='modal__header'>
					<h3 className='modal__title'>
						{t('medical:patients.register.staff.searchTitle')}
					</h3>
					<button
						type='button'
						className='modal__close'
						onClick={onClose}
						aria-label={t('common:actions.close')}
					>
						<X size={20} />
					</button>
				</div>
				<div className='modal__body overflow-y-auto max-h-80'>
					<div className='filter-bar__search filter-bar__search--icon mb-3 !sticky top-0 bg-white z-10'>
						<Search size={16} />
						<input
							type='text'
							className='input'
							placeholder={t(
								'medical:patients.register.staff.searchPlaceholder',
							)}
							value={search}
							onChange={(e) => onSearchChange(e.target.value)}
							autoFocus
						/>
					</div>
					<ul className='divide-y divide-gray-100'>
						{filtered.map((s) => (
							<li key={s.email}>
								<button
									type='button'
									className='flex items-center gap-2 w-full px-3 py-2.5 text-left hover:bg-gray-50 disabled:opacity-40'
									onClick={() => onAdd(s)}
									disabled={!!assigned.find((a) => a.email === s.email)}
								>
									<strong className='text-sm'>{s.name}</strong>
									<span className='badge badge--gray badge--xs'>
										{s.role === 'DOCTOR'
											? t('common:roles.doctor')
											: s.role === 'NURSE'
												? t('common:roles.nurse')
												: s.role}
									</span>
									<span className='text-xs text-gray-400'>{s.department}</span>
								</button>
							</li>
						))}
						{filtered.length === 0 && (
							<li className='px-3 py-6 text-center text-sm text-gray-400'>
								{t('common:error.generic')}
							</li>
						)}
					</ul>
				</div>
			</div>
		</div>
	);
}
